"use client";
import "./header.css";
import Link from "next/link";
import React, { useState, useEffect, useRef } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { fetchAPI, getAccessToken, getMemberAccessToken } from "../../utils/api";
import { openOBBookingLink, openSAResortsSSO } from "../../utils/obBookingUtils";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Button from "@mui/material/Button";

function Header() {
  const params = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const closeTimeoutRef = useRef(null);
  const [isMobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [userDetails, setUserDetails] = useState({});
  const [token, setToken] = useState(null);
  const [open, setOpen] = useState(false);
  const [popupForPlusBenefits, setPopupForPlusBenefits] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [memberType, setMemberType] = useState(null);
  const [hasToken, setHasToken] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setPopupForPlusBenefits(false);
  };

  const handleLogin = () => {
    window.location.href = "/?p=login";
  };

  const openLoginModal = () => setIsOpen(true);
  const openBookingModal = () => setIsBookingOpen(true);
  const closeBookingModal = () => setIsBookingOpen(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);

      // Step 1: Initial login with Company = 7
      const loginRes = await fetchAPI(
        "/DetailsForOauthLogin",
        "POST",
        {
          Email: email,
          Company: 7,
        },
        token
      );

      if (loginRes?.Authenticated === true) {
        let finalLoginRes = loginRes;

        // Save initial member ID for GetMemberOBPToken requests
        if (typeof window !== "undefined") {
          window.localStorage.setItem("ob-init-member-id", loginRes.MemberID);
        }

        // Step 2: If OBPlus is true, make second call with Company = 56
        if (loginRes.OBPlus === true) {
          try {
            const obPlusLoginRes = await fetchAPI(
              "/DetailsForOauthLogin",
              "POST",
              {
                Email: email,
                Company: 56,
              },
              token
            );

            if (obPlusLoginRes?.Authenticated === true) {
              finalLoginRes = obPlusLoginRes;
            }
          } catch (obPlusError) {
            console.error("Error fetching OBPlus details:", obPlusError);
            // Continue with original response if OBPlus call fails
          }
        }

        setIsLoading(false);
        setEmail("");
        setPassword("");
        setStatus("success");
        setStatusMessage("Successfully logged in!!!");
        setTimeout(() => {
          setStatus(null);
        }, 1000);

        if (typeof window !== "undefined") {
          window.localStorage.setItem("offbeat-token", finalLoginRes.Token);
          window.localStorage.setItem(
            "offbeat-member-id",
            finalLoginRes.MemberID
          );
          const newMemberType = finalLoginRes.OBPlus ? "ob+" : "obhc";
          window.localStorage.setItem(
            "offbeat-member-type",
            newMemberType
          );
          setHasToken(true);
          setMemberType(newMemberType);
        }

        try {
          const memberToken = await getMemberAccessToken(email, password);
          if (typeof window !== "undefined") {
            window.localStorage.setItem("offbeat-sso-username", memberToken.userName);
            window.localStorage.setItem("offbeat-sso-access-token", memberToken.access_token);
          }
        } catch (err) {
          // SSO token unavailable; SA Resorts SSO link will prompt login
        }

        closeLoginModal();
      } else {
        setIsLoading(false);
        setStatus("error");
        setStatusMessage("Username or password is incorrect");

        setTimeout(() => {
          setStatus(null);
        }, 3000);
      }
    } catch (error) {
      setIsLoading(false);
      setStatus("error");
      setStatusMessage("Whoops!!! Something went wrong, try again");
    }
  };

  // Event handler to toggle the state
  const toggleNav = () => {
    setMobileDropdownOpen(!isMobileDropdownOpen);
  };

  const handleLinkClick = () => {
    setIsActive(false); // Close the mobile menu when any link is clicked
  };

  const handleOutsideClick = (event) => {
    if (isOpen && !event.target.closest(".login-Pop-cont")) {
      setIsOpen(false);
    }
    if (isBookingOpen && !event.target.closest(".booking-Pop-cont")) {
      setIsBookingOpen(false);
    }
  };

  useEffect(() => {
    // Add event listener on document load
    document.addEventListener("click", handleOutsideClick);

    // Cleanup function to remove listener on component unmount
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [isOpen, isBookingOpen]);

  // Improved closeLoginModal function with conditional check
  const closeLoginModal = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    setIsClient(true);
    setMemberType(window.localStorage.getItem("offbeat-member-type"));
    setHasToken(!!window.localStorage.getItem("offbeat-token"));
  }, []);

  useEffect(() => {
    if (params.get("p") === "login") {
      openLoginModal();
    }
  }, []);

  useEffect(() => {
    getAccessToken()
      .then((res) => {
        setToken(res);
        typeof window !== "undefined" &&
          window.localStorage.setItem("offbeat-bearer-token", res);
      })
      .catch((err) => {});
  }, []);

  useEffect(() => {
    if (
      (typeof window !== "undefined" &&
        window.localStorage.getItem("offbeat-token")) ||
      status === "success"
    ) {
      fetchAPI(
        `/ContactDetail?MemberID=${window.localStorage.getItem(
          "offbeat-member-id"
        )}`,
        "GET",
        null,
        token ||
          (typeof window !== "undefined" &&
            window.localStorage.getItem("offbeat-bearer-token"))
      )
        .then((res) => {
          res.MemberID = window.localStorage.getItem("offbeat-member-id");
          setUserDetails(res);
        })
        .catch((err) => {});
    }
  }, [status, token]);

  const getOBBookingLink = async (service) => {
    await openOBBookingLink(service, setOpen);
  };

  const getSAResortsSSOLink = () => {
    openSAResortsSSO(setOpen);
  };

  const isSpecialPage =
    pathname.includes("/account") ||
    pathname.includes("/terms-of-service") ||
    pathname.includes("/booking-cancellations") ||
    pathname.includes("/privacy-policy") ||
    pathname.includes("/refund-and-returns");

  const handleMouseEnter = () => {
    clearTimeout(closeTimeoutRef.current);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };

  const openForgotPasswordForm = (e) => {
    e.preventDefault();
    // close the login modal
    setIsOpen(false);
    router.push("/forgot-password");
  };

  return (
    <header className="header">
      {status === "success" && (
        <Alert severity="success">{statusMessage}</Alert>
      )}
      {status === "error" && <Alert severity="error">{statusMessage}</Alert>}
      <div className={`bot-bar ${isSpecialPage ? "account-bg-menu" : ""}`}>
        <div className="logo">
          <Link onClick={handleLinkClick} href="/">
            <img
              src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Off%20Beat%20Landscape.jpg"
              width="200px"
              height="auto"
              alt="OFFBEAT Logo"
            />
          </Link>
        </div>
        <div className="filler"></div>
        <div className="button-cont">
          <div
            className="dropdown"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button className="dropdown-button">Explore now</button>

            {isDropdownOpen && (
              <nav className="dropdown-menu">
                <ul className="nav-list">
                  <li className="nav-heading">STANDARD BENEFITS</li>
                  <li
                    className={`nav-item ${
                      pathname === "/benefits#sa-resorts" ? "activeLink" : ""
                    }`}
                  >
                    <Link
                      onClick={(e) => {
                        e.preventDefault();
                        getSAResortsSSOLink();
                      }}
                      href="/#offbeatresorts"
                    >
                      <div className="nav-icon-cont">
                        <img
                          src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Resort2.png"
                          alt="Off Beat Resorts"
                          className="nav-icon"
                        />
                      </div>
                      Off Beat Resorts
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${
                      pathname === "/benefits#international-exchanges"
                        ? "activeLink"
                        : ""
                    }`}
                  >
                    <Link href="/benefits#international-exchanges">
                      <div className="nav-icon-cont">
                        <img
                          src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Exchanges.png"
                          alt="Off Beat Exchanges"
                          className="nav-icon"
                        />
                      </div>
                      Off Beat Exchanges
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${
                      pathname === "/benefits#msc-splendida" ? "activeLink" : ""
                    }`}
                  >
                    <Link href="/benefits#msc-splendida">
                      <div className="nav-icon-cont">
                        <img
                          src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Cruise.png"
                          alt="SA Cruises"
                          className="nav-icon"
                        />
                      </div>
                      SA Cruises
                    </Link>
                  </li>

                  <hr />

                  <li className="nav-heading">PLUS BENEFITS</li>
                  <div>
                    <li
                      className={`nav-item ${
                        pathname === "/lifestyle#dining-&-coffee"
                          ? "activeLink"
                          : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            e.preventDefault();
                            setPopupForPlusBenefits(true);
                          }

                          handleLinkClick();
                        }}
                        href="/lifestyle#dining-&-coffee"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Meals.png"
                            alt="Meals"
                            className="nav-icon"
                          />
                        </div>
                        Meals
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/lifestyle#movies" ? "activeLink" : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            e.preventDefault();
                            setPopupForPlusBenefits(true);
                          }

                          handleLinkClick();
                        }}
                        href="/lifestyle#movies"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Movies.png"
                            alt="Movies"
                            className="nav-icon"
                          />
                        </div>
                        Movies
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/#hotels" ? "activeLink" : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          e.preventDefault();
                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            setPopupForPlusBenefits(true);

                            return;
                          }

                          getOBBookingLink("/home");
                        }}
                        href="/#hotels"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Resort2.png"
                            alt="Hotels"
                            className="nav-icon"
                          />
                        </div>
                        Hotels
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/#international-resorts"
                          ? "activeLink"
                          : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          e.preventDefault();
                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            setPopupForPlusBenefits(true);

                            return;
                          }

                          getOBBookingLink("/home/resorts");
                        }}
                        href="/#international-resorts"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Resort.png"
                            alt="Int. Resorts"
                            className="nav-icon"
                          />
                        </div>
                        Int. Resorts
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/#card-parent" ? "activeLink" : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          e.preventDefault();
                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            setPopupForPlusBenefits(true);

                            return;
                          }

                          getOBBookingLink("/cruises");
                        }}
                        href="/#cruises"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Cruise2.pnghttps://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Cruise.png"
                            alt="Int. Cruises"
                            className="nav-icon"
                          />
                        </div>
                        Int. Cruises
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/#card-parent" ? "activeLink" : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          e.preventDefault();

                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            setPopupForPlusBenefits(true);

                            return;
                          }

                          getOBBookingLink("/home/flight");
                        }}
                        href="/#flight"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Flights.png"
                            alt="Flights"
                            className="nav-icon"
                          />
                        </div>
                        Flights
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/#card-parent" ? "activeLink" : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          e.preventDefault();
                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            setPopupForPlusBenefits(true);

                            return;
                          }

                          getOBBookingLink("/home/cars/");
                        }}
                        href="/#cars"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Cars.png"
                            alt="Car Hire"
                            className="nav-icon"
                          />
                        </div>
                        Car Hire
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/#card-parent" ? "activeLink" : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          e.preventDefault();

                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            setPopupForPlusBenefits(true);

                            return;
                          }

                          getOBBookingLink("/home/activities");
                        }}
                        href="/#activities"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Activities.png"
                            alt="Activities"
                            className="nav-icon"
                          />
                        </div>
                        Activities
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/lifestyle#spa" ? "activeLink" : ""
                      }`}
                    >
                      <Link
                        onClick={(e) => {
                          if (
                            typeof window !== "undefined" &&
                            window.localStorage.getItem(
                              "offbeat-member-type"
                            ) === "obhc"
                          ) {
                            e.preventDefault();
                            setPopupForPlusBenefits(true);
                          }

                          handleLinkClick();
                        }}
                        href="/lifestyle#spa"
                      >
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Spa.png"
                            alt="Spa"
                            className="nav-icon"
                          />
                        </div>
                        Spa
                      </Link>
                    </li>
                    <li
                      className={`nav-item ${
                        pathname === "/deals-4-U" ? "activeLink" : ""
                      }`}
                    >
                      <Link href="/deals-4-U">
                        <div className="nav-icon-cont">
                          <img
                            src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Deals.png"
                            alt="Deals-4-U"
                            className="nav-icon"
                          />
                        </div>
                        Deals-4-U
                      </Link>
                    </li>
                  </div>
                </ul>
              </nav>
            )}
          </div>
          <div className="horizontal-line"></div>
          {isClient && memberType !== "ob+" && (
            <a className="upgrade-link" href="/join-now">
              Join Now
            </a>
          )}
          {isClient && hasToken ? (
            <a
              className="nav-item nav-space"
              onClick={(e) => {
                e.preventDefault();

                router.push("/account");
              }}
            >
              <img
                src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/User.png"
                alt="Login Icon"
              />
              {"  "}Hi{" "}
              {userDetails?.FirstName === ""
                ? userDetails?.Family?.[0]?.FirstName
                : userDetails?.FirstName}
            </a>
          ) : (
            <>
              <a className="register-link" href="/register">
                Register
              </a>
              <a className="nav-item nav-space" onClick={openLoginModal}>
                Login
              </a>
            </>
          )}
        </div>
        {isOpen && (
          <div className="login-Pop-cont">
            <button className="close" onClick={closeLoginModal}>
              &times;
            </button>
            <h2>Login</h2>
            <form onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email">Your Email Address</label>
                <input
                  type="text"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="password">Your Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="remember-me-wrapper-flex">
                <div className="remember-2">
                  <label
                    htmlFor="forgotPassword"
                    className="forgot-pass-link"
                    onClick={openForgotPasswordForm}
                  >
                    I forgot my password
                  </label>
                </div>
                <div className="remember-1">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />{" "}
                  <label htmlFor="rememberMe">Remember Me</label>
                </div>
              </div>

              {/* <div>
                  <a href="/reactivate-details">I forgot my password</a>
                </div> */}
              <button
                className="btn btn-login"
                type="submit"
                disabled={!email || !password || isLoading ? true : false}
              >
                {isLoading ? "Please Wait..." : "Sign In"}
              </button>
            </form>
            <div className="policies">
              <a href="#" target="_blank">
                Privacy Policy
              </a>
              |
              <a href="#" target="_blank">
                Terms of Use
              </a>
            </div>
          </div>
        )}
        {isBookingOpen && (
          <div className="booking-Pop-cont">
            <button className="close" onClick={closeBookingModal}>
              &times;
            </button>
            <h2>MAKE THE BEST OF YOUR BENEFITS</h2>
            <div className="booking-card-cont">
              <div className="booking-card ob-pill">
                <Link
                  href={"/"}
                  onClick={(e) => {
                    e.preventDefault();
                    getOBBookingLink("/home");
                  }}
                >
                  <span className="pill">Off Beat</span>
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/SA-Resort.jpeg"
                    alt="SA Resorts"
                  />
                  <p>SA Resorts</p>
                </Link>
              </div>
              <div className="booking-card card-overlay ob-pill">
                <div className="iao-overlay-cont">
                  <span className="pill">Off Beat</span>
                  <img
                    src="https://nouf-flp-images.s3.af-south-1.amazonaws.com/Cruises-Book.png"
                    alt="SA Cruises"
                  />
                  <div className="iao-overlay">
                    <span style={{ fontWeight: "bold" }}>Click to Call</span>
                  </div>
                </div>
                <p>SA Cruises</p>
              </div>
              <div className="booking-card card-overlay ob-pill">
                <div className="iao-overlay-cont">
                  <span className="pill">Off Beat</span>
                  <img
                    src="https://nouf-flp-images.s3.af-south-1.amazonaws.com/Flights-Book.png"
                    alt="International Resort Exchanges"
                  />
                  <div className="iao-overlay">
                    <span style={{ fontWeight: "bold" }}>Click to Call</span>
                  </div>
                </div>
                <p>International Resort Exchanges</p>
              </div>
              <div className="booking-card obp-pill">
                <Link
                  href={"/"}
                  onClick={(e) => {
                    e.preventDefault();
                    getOBBookingLink("/home/resorts");
                  }}
                >
                  <span className="pill">Off Beat Plus</span>
                  <img
                    src="/images/Holidays-Hotels-Book.png"
                    alt="Hotels & Resorts"
                  />
                  <p>Hotels & Resorts</p>
                </Link>
              </div>
              <div className="booking-card obp-pill">
                <Link
                  href={"/"}
                  onClick={(e) => {
                    e.preventDefault();
                    getOBBookingLink("/cruises");
                  }}
                >
                  <span className="pill">Off Beat Plus</span>
                  <img
                    src="https://nouf-flp-images.s3.af-south-1.amazonaws.com/Cruises-Book.png"
                    alt="International Cruises"
                  />
                  <p>International Cruises</p>
                </Link>
              </div>
              <div className="booking-card obp-pill">
                <Link
                  href={"/"}
                  onClick={(e) => {
                    e.preventDefault();
                    getOBBookingLink("/home/flight");
                  }}
                >
                  <span className="pill">Off Beat Plus</span>
                  <img
                    src="https://nouf-flp-images.s3.af-south-1.amazonaws.com/Flights-Book.png"
                    alt="Flights"
                  />
                  <p>Flights</p>
                </Link>
              </div>
              <div className="booking-card obp-pill">
                <Link
                  href={"/"}
                  onClick={(e) => {
                    e.preventDefault();
                    getOBBookingLink("/home/activities");
                  }}
                >
                  <span className="pill">Off Beat Plus</span>
                  <img
                    src="/images/Tours-Activities-Book.png"
                    alt="Tours & Activities"
                  />
                  <p>Tours & Activities</p>
                </Link>
              </div>
              <div className="booking-card obp-pill">
                <Link
                  href={"/"}
                  onClick={(e) => {
                    e.preventDefault();
                    getOBBookingLink("/home/cars/");
                  }}
                >
                  <span className="pill">Off Beat Plus</span>
                  <img src="/images/Car-Hire-Book.png" alt="Car Hire" />
                  <p>Car Hire</p>
                </Link>
              </div>
              <div className="booking-card obp-pill">
                <Link href={"/deals-4-U"}>
                  <span className="pill">Off Beat Plus</span>
                  <img src="/images/Deals-4-U-Book.png" alt="Deals-4-U" />
                  <p>Deals-4-U</p>
                </Link>
              </div>
              <div className="booking-card card-overlay obp-pill">
                <div className="iao-overlay-cont">
                  <span className="pill">Off Beat Plus</span>
                  <img src="/images/Spa-Book.png" alt="Spa" />
                  <div className="iao-overlay">
                    <span style={{ fontWeight: "bold" }}>In App Only</span>
                  </div>
                </div>
                <p>Spa</p>
              </div>
              <div className="booking-card card-overlay obp-pill">
                <div className="iao-overlay-cont">
                  <span className="pill">Off Beat Plus</span>
                  <img src="/images/Movies-Book.png" alt="Movies" />
                  <div className="iao-overlay">
                    <span style={{ fontWeight: "bold" }}>In App Only</span>
                  </div>
                </div>
                <p>Movies</p>
              </div>
              <div className="booking-card card-overlay obp-pill">
                <div className="iao-overlay-cont">
                  <span className="pill">Off Beat Plus</span>
                  <img
                    src="/images/Dining-Coffee-Book.png"
                    alt="Dining & Coffee"
                  />
                  <div className="iao-overlay">
                    <span style={{ fontWeight: "bold" }}>In App Only</span>
                  </div>
                </div>
                <p>Dining & Coffee</p>
              </div>
            </div>
            <div className="GTA-cont">
              <h3>Get the App</h3>
              <img
                src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/apple-app-store-logo.png"
                alt="Download on the App Store"
              ></img>
              <img
                src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/google-play-badge-(1).png"
                alt="Get it on Google Play"
              ></img>
            </div>
          </div>
        )}
        <button className="burger" id="burger" onClick={toggleNav}>
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      {isMobileDropdownOpen && (
        <nav className="mobi dropdown-menu">
          <ul className="nav-list">
            <li className="nav-item" onClick={toggleNav}>
              {isClient && hasToken ? (
                <a
                  onClick={(e) => {
                    e.preventDefault();

                    router.push("/account");
                  }}
                >
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/User.png"
                    alt="Login Icon"
                  />
                  {"  "}Hi{" "}
                  {userDetails?.FirstName === ""
                    ? userDetails?.Family?.[0]?.FirstName
                    : userDetails?.FirstName}
                </a>
              ) : (
                <a className="nav-item" href="#" onClick={openLoginModal}>
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/User.png"
                    alt="Login Icon"
                  />{" "}
                  Login
                </a>
              )}
            </li>
            {isClient && memberType !== "ob+" && (
              <li className="nav-item" onClick={toggleNav}>
                <a className="upgrade-link" href="/join-now">
                  Join Now
                </a>
              </li>
            )}

            <hr />

            <li className="nav-heading">STANDARD BENEFITS</li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/benefits#sa-resorts" ? "activeLink" : ""
              }`}
            >
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  getSAResortsSSOLink();
                }}
                href="/benefits#sa-resorts"
              >
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Resort2.png"
                    alt="Off Beat Resorts"
                    className="nav-icon"
                  />
                </div>
                Off Beat Resorts
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/benefits#international-exchanges"
                  ? "activeLink"
                  : ""
              }`}
            >
              <Link
                onClick={handleLinkClick}
                href="/benefits#international-exchanges"
              >
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Exchanges.png"
                    alt="Off Beat Exchanges"
                    className="nav-icon"
                  />
                </div>
                Off Beat Exchanges
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/benefits#msc-splendida" ? "activeLink" : ""
              }`}
            >
              <Link onClick={handleLinkClick} href="/benefits#msc-splendida">
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Cruise.png"
                    alt="SA Cruises"
                    className="nav-icon"
                  />
                </div>
                SA Cruises
              </Link>
            </li>

            <hr />

            <li className="nav-heading">PLUS BENEFITS</li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/lifestyle#dining-&-coffee" ? "activeLink" : ""
              }`}
            >
              <Link onClick={handleLinkClick} href="/lifestyle#dining-&-coffee">
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Meals.png"
                    alt="Meals"
                    className="nav-icon"
                  />
                </div>
                Meals
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/lifestyle#movies" ? "activeLink" : ""
              }`}
            >
              <Link onClick={handleLinkClick} href="/lifestyle#movies">
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Movies.png"
                    alt="Movies"
                    className="nav-icon"
                  />
                </div>
                Movies
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/#hotels" ? "activeLink" : ""
              }`}
            >
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  getOBBookingLink("/home");
                }}
                href="/#hotels"
              >
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Resort2.png"
                    alt="Hotels"
                    className="nav-icon"
                  />
                </div>
                Hotels
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/#international-resorts" ? "activeLink" : ""
              }`}
            >
              <Link onClick={handleLinkClick} href="/#international-resorts">
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Resort.png"
                    alt="Int. Resorts"
                    className="nav-icon"
                  />
                </div>
                Int. Resorts
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/#card-parent" ? "activeLink" : ""
              }`}
            >
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  getOBBookingLink("/cruises");
                }}
                href="/#card-parent"
              >
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Cruise2.pnghttps://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Cruise.png"
                    alt="Int. Cruises"
                    className="nav-icon"
                  />
                </div>
                Int. Cruises
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/#card-parent" ? "activeLink" : ""
              }`}
            >
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  getOBBookingLink("/home/flight");
                }}
                href="/#card-parent"
              >
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Flights.png"
                    alt="Flights"
                    className="nav-icon"
                  />
                </div>
                Flights
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/#card-parent" ? "activeLink" : ""
              }`}
            >
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  getOBBookingLink("/home/cars/");
                }}
                href="/#card-parent"
              >
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Cars.png"
                    alt="Car Hire"
                    className="nav-icon"
                  />
                </div>
                Car Hire
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/#card-parent" ? "activeLink" : ""
              }`}
            >
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  getOBBookingLink("/home/activities");
                }}
                href="/#card-parent"
              >
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Activities.png"
                    alt="Activities"
                    className="nav-icon"
                  />
                </div>
                Activities
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/lifestyle#spa" ? "activeLink" : ""
              }`}
            >
              <Link onClick={handleLinkClick} href="/lifestyle#spa">
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Spa.png"
                    alt="Spa"
                    className="nav-icon"
                  />
                </div>
                Spa
              </Link>
            </li>
            <li
              onClick={toggleNav}
              className={`nav-item ${
                pathname === "/deals-4-U" ? "activeLink" : ""
              }`}
            >
              <Link href="/deals-4-U">
                <div className="nav-icon-cont">
                  <img
                    src="https://off-beat-cdn.blr1.cdn.digitaloceanspaces.com/Dropdown-icons/Deals.png"
                    alt="Deals-4-U"
                    className="nav-icon"
                  />
                </div>
                Deals-4-U
              </Link>
            </li>
          </ul>
        </nav>
      )}

      <Dialog
        open={open}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{ height: "200px !important" }}
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            You are not logged in. Please login to proceed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleLogin}>Proceed to Login</Button>
        </DialogActions>
      </Dialog>

      {/* for ob+ popup */}
      <Dialog
        open={popupForPlusBenefits}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{ height: "200px !important" }}
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            To access this benefit, please Upgrade to Off Beat Plus
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (typeof window !== "undefined") {
                window.location.href = "/join-now";
              }
            }}
          >
            Proceed to Upgrade
          </Button>
        </DialogActions>
      </Dialog>
    </header>
  );
}

export default Header;
